import { Button, Chip, InputAdornment, makeStyles, Paper, TextField, Tooltip, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { DataGrid, GridColDef, GridOverlay } from "@material-ui/data-grid";
import React, { useMemo, useState } from "react";
import { MdAdd, MdSearch } from "react-icons/md";

const useStyles = makeStyles(theme => ({
    toolbar: {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "center",
        gap: theme.spacing(2),
        padding: theme.spacing(2),
    },
    title: {
        fontWeight: 700,
    },
    toolbarRight: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    search: {
        minWidth: 260,
    },
    grid: {
        border: "none",
        borderTop: `1px solid ${theme.palette.divider}`,
        borderRadius: 0,
        "& .MuiDataGrid-row": {
            cursor: "pointer",
        },
        "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
        },
        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none",
        },
    },
    chips: {
        display: "flex",
        gap: 4,
        overflow: "hidden",
    },
    alert: {
        margin: theme.spacing(0, 2, 2, 2),
    },
    filters: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(0, 2, 2, 2),
    },
}));

export interface EntityTableProps<R> {
    title?: string;
    rows: R[];
    columns: GridColDef[];
    getRowId: (row: R) => string;
    searchText?: (row: R) => string;
    searchPlaceholder?: string;
    onRowClick?: (row: R) => void;
    createLabel?: string;
    onCreate?: () => void;
    loading?: boolean;
    error?: string;
    onRetry?: () => void;
    emptyText?: string;
    /** Extra filter controls shown below the toolbar. */
    filters?: React.ReactNode;
    /** Controlled search text, for when the parent needs the searched rows. */
    search?: string;
    onSearchChange?: (search: string) => void;
    /** Highlights a row, e.g. the one opened in a side panel. */
    selectedRowId?: string;
}

/** The rows matching a search text, as shown by EntityTable. */
export function filterBySearch<R>(rows: R[], search: string, searchText?: (row: R) => string): R[] {
    const needle = search.trim().toLowerCase();
    if (!needle || !searchText) return rows;
    return rows.filter(row => searchText(row).toLowerCase().includes(needle));
}

export function EntityTable<R>({
    title, rows, columns, getRowId, searchText, searchPlaceholder = "Search", onRowClick,
    createLabel, onCreate, loading, error, onRetry, emptyText = "Nothing here yet", filters,
    search: controlledSearch, onSearchChange, selectedRowId,
}: EntityTableProps<R>) {
    const classes = useStyles();
    const [localSearch, setLocalSearch] = useState("");
    const search = controlledSearch ?? localSearch;
    const setSearch = onSearchChange ?? setLocalSearch;
    const [pageSize, setPageSize] = useState(25);

    const filteredRows = useMemo(() => filterBySearch(rows, search, searchText), [rows, search, searchText]);

    const NoRows = () => (
        <GridOverlay>
            <Typography color="textSecondary">{search ? `No results for "${search}"` : emptyText}</Typography>
        </GridOverlay>
    );

    return (
        <Paper variant="outlined">
            {(title || searchText || onCreate) && (
                <div className={classes.toolbar}>
                    {title ? <Typography variant="h6" component="h2" className={classes.title}>{title}</Typography> : <span />}
                    <div className={classes.toolbarRight}>
                        {searchText && (
                            <TextField
                                className={classes.search}
                                size="small"
                                variant="outlined"
                                placeholder={searchPlaceholder}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch /></InputAdornment> }}
                            />
                        )}
                        {onCreate && (
                            <Button variant="contained" color="primary" startIcon={<MdAdd />} onClick={onCreate}>
                                {createLabel ?? "New"}
                            </Button>
                        )}
                    </div>
                </div>
            )}
            {filters && <div className={classes.filters}>{filters}</div>}
            {error && (
                <Alert
                    severity="error"
                    className={classes.alert}
                    action={onRetry ? <Button color="inherit" size="small" onClick={onRetry}>Retry</Button> : undefined}
                >
                    {error}
                </Alert>
            )}
            <DataGrid
                className={classes.grid}
                autoHeight
                density="compact"
                rows={filteredRows as any[]}
                columns={columns}
                getRowId={row => getRowId(row as R)}
                loading={loading}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                rowsPerPageOptions={[10, 25, 50, 100]}
                disableSelectionOnClick
                selectionModel={selectedRowId ? [selectedRowId] : []}
                hideFooterSelectedRowCount
                onRowClick={params => onRowClick && onRowClick(params.row as R)}
                components={{ NoRowsOverlay: NoRows }}
            />
        </Paper>
    );
}

/** Column cell that renders a list of ids (e.g. admin emails) as compact chips. */
export const ChipsCell: React.FC<{ values: string[] }> = ({ values }) => {
    const classes = useStyles();
    if (values.length === 0) {
        return <Typography variant="body2" color="error">None</Typography>;
    }
    const shown = values.slice(0, 2);
    return (
        <Tooltip title={values.join(", ")}>
            <div className={classes.chips}>
                {shown.map(value => <Chip key={value} size="small" label={value} />)}
                {values.length > shown.length && <Chip size="small" label={`+${values.length - shown.length}`} />}
            </div>
        </Tooltip>
    );
};
